import { reactive } from 'vue'
import { api } from '@/services/api.js'
import { cacheTags, getCachedTags, hasTagsCache, updateCachedTag, deleteCachedTag, addCachedTag } from '@/services/offlineQueue.js'

// Clear all tags state (called on logout)
export function clearTagsState() {
  tagsState.tags = []
  tagsState.pinnedTags = []
  tagsState.error = null
}

export const tagsState = reactive({
  tags: [],
  pinnedTags: [],
  loading: false,
  error: null
})

// Load all tags from server (with cache)
export async function loadAllTags() {
  tagsState.loading = true
  
  try {
    // Load from cache first for instant display
    const cached = await getCachedTags()
    if (cached && cached.length > 0) {
      tagsState.tags = cached.map(t => ({
        id: t.id,
        name: t.name,
        count: t.usage_count || t.count || 0
      }))
      tagsState.loading = false
      console.log(`Loaded ${cached.length} tags from cache`)
    }
    
    // Fetch from server in background
    const response = await api.get('/tags')
    const serverTags = response.data || response || []
    
    // Only update if data changed
    const newTags = serverTags.map(t => ({
      id: t.id,
      name: t.name,
      count: t.count
    }))
    
    const isSame = JSON.stringify(tagsState.tags) === JSON.stringify(newTags)
    if (!isSame) {
      tagsState.tags = newTags
      console.log(`Updated tags from server: ${newTags.length} tags`)
    }
    
    // Update cache in background
    cacheTags(serverTags).catch(console.error)
  } catch (error) {
    // If server fails but we have cache, continue with cached data
    if (tagsState.tags.length === 0) {
      tagsState.error = error.message
      console.error('Failed to load tags:', error)
    } else {
      console.log('Using cached tags, server unreachable')
    }
  } finally {
    tagsState.loading = false
  }
}

// Load pinned tags from backend
export async function loadPinnedTags() {
  try {
    const response = await api.get('/pinned-tags')
    tagsState.pinnedTags = response.data || []
  } catch (error) {
    console.error('Failed to load pinned tags:', error)
    tagsState.pinnedTags = []
  }
}

// Pin a tag
export async function pinTag(tagName) {
  try {
    await api.post('/pinned-tags', { tag: tagName })
    if (!tagsState.pinnedTags.includes(tagName)) {
      tagsState.pinnedTags.push(tagName)
    }
  } catch (error) {
    console.error('Failed to pin tag:', error)
    throw error
  }
}

// Unpin a tag
export async function unpinTag(tagName) {
  try {
    await api.delete(`/pinned-tags/${encodeURIComponent(tagName)}`)
    tagsState.pinnedTags = tagsState.pinnedTags.filter(t => t !== tagName)
  } catch (error) {
    console.error('Failed to unpin tag:', error)
    throw error
  }
}

// Reorder pinned tags
export async function reorderPinnedTags(newOrder) {
  try {
    await api.put('/pinned-tags/reorder', { tags: newOrder })
    tagsState.pinnedTags = newOrder
  } catch (error) {
    console.error('Failed to reorder tags:', error)
    throw error
  }
}

// Extract all unique tags from notes and merge with server tags (local computation)
export function loadTags(notes) {
  // Don't overwrite server-provided tags - just update counts
  if (tagsState.tags.length > 0 && tagsState.tags[0].id && tagsState.tags[0].id.length > 10) {
    // We have server tags with real UUIDs, just update counts
    const tagCount = {}
    notes.forEach(note => {
      if (note.tags && Array.isArray(note.tags)) {
        note.tags.forEach(tag => {
          const tagLower = tag.toLowerCase().trim()
          tagCount[tagLower] = (tagCount[tagLower] || 0) + 1
        })
      }
    })
    
    // Update counts on existing tags
    tagsState.tags = tagsState.tags.map(tag => ({
      ...tag,
      count: tagCount[tag.name.toLowerCase()] || 0
    })).filter(tag => tag.count > 0)
    
    return
  }
  
  // Fallback: No server tags yet, build from notes
  tagsState.loading = true
  
  try {
    const tagMap = new Map()
    
    notes.forEach(note => {
      if (note.tags && Array.isArray(note.tags)) {
        note.tags.forEach(tag => {
          const tagLower = tag.toLowerCase().trim()
          if (tagLower) {
            const existing = tagMap.get(tagLower)
            if (existing) {
              existing.count++
              existing.noteIds.add(note.id)
            } else {
              tagMap.set(tagLower, {
                id: tagLower,
                name: tag,
                count: 1,
                noteIds: new Set([note.id])
              })
            }
          }
        })
      }
    })
    
    // Convert to array and sort by count (descending)
    tagsState.tags = Array.from(tagMap.values())
      .sort((a, b) => b.count - a.count)
  } catch (error) {
    tagsState.error = error.message
    console.error('Failed to load tags:', error)
  } finally {
    tagsState.loading = false
  }
}

// Get notes by tag
export function getNotesByTag(notes, tag) {
  return notes.filter(note => 
    note.tags && note.tags.some(t => t.toLowerCase() === tag.toLowerCase())
  )
}
